import React from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import DashboardLayout from './layout/DashboardLayout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { MaintenanceProvider, useMaintenance } from './contexts/MaintenanceContext';
import MaintenancePage from './pages/Maintenance';

// Shared pages
import Home             from './pages/Home';
import Login            from './pages/Login';
import Deliveries       from './pages/Deliveries';
import ActiveDeliveries from './pages/ActiveDeliveries';
import Orders           from './pages/Orders';
import CreateUser       from './pages/CreateUser';
import Drivers          from './pages/Drivers';
import Customers        from './pages/Customers';
import CustomerOrders   from './pages/CustomerOrders';
import Pickers          from './pages/Pickers';
import HelpPage         from './pages/HelpPage';
import OrderDetails     from './pages/OrderDetails';
import Dispatch         from './pages/Dispatch';
import Analytics        from './pages/Analytics';
import Collections      from './pages/Collections';
import CakeConfigPage   from './pages/CakeConfigPage';

// Inventory pages
import ProductsPage         from './pages/inventory/ProductsPage';
import CategoriesPage       from './pages/inventory/CategoriesPage';
import PriceHistoryPage     from './pages/inventory/PriceHistoryPage';
import SpecialsPage         from './pages/inventory/SpecialsPage';
import StoresPage           from './pages/inventory/StoresPage';
import DeliveryFeesPage     from './pages/DeliveryFeesPage';
import CollectionPointsPage from './pages/CollectionPointsPage';
import FaqsPage             from './pages/FaqsPage';

// Public pages
import AccountDeletionRequest from './pages/AccountDeletionRequest';
import AccountDeletionVerify  from './pages/AccountDeletionVerify';
import ResetPassword          from './pages/ResetPasswordPage';

// ── Maintenance gate ──────────────────────────────────────────────────────────

function MaintenanceGatedRoute({ children }: { children: React.ReactNode }) {
  const { isActive, loading: maintenanceLoading } = useMaintenance();
  const { profile, loading: authLoading } = useAuth();

  if (maintenanceLoading || authLoading) return null;

  const isAdmin = profile?.role === 'admin';
  if (isActive && !isAdmin) return <MaintenancePage />;

  return <>{children}</>;
}

// ── App ───────────────────────────────────────────────────────────────────────

function App() {
  return (
      <Sentry.ErrorBoundary
          fallback={({ error, resetError }) => (
              <div className="p-4 error-container">
                <h1>Something went wrong</h1>
                <p>{error instanceof Error ? error.message : 'An unexpected error occurred'}</p>
                <button className="px-4 py-2 bg-blue-500 text-white rounded" onClick={resetError}>
                  Try again
                </button>
              </div>
          )}>
        <AuthProvider>
          <MaintenanceProvider>
            <BrowserRouter>
              <Routes>
                {/* Public routes */}
                <Route path="/account-deletion"        element={<AccountDeletionRequest />} />
                <Route path="/account-deletion/verify" element={<AccountDeletionVerify />} />
                <Route path="/reset-password"          element={<ResetPassword />} />
                <Route path="/login"                   element={<Login />} />

                {/* Dashboard layout */}
                <Route path="/" element={<MaintenanceGatedRoute><DashboardLayout /></MaintenanceGatedRoute>}>
                  <Route index element={<ProtectedRoute><Home /></ProtectedRoute>} />

                  {/* Orders */}
                  <Route path="orders"     element={<ProtectedRoute><Orders /></ProtectedRoute>} />
                  <Route path="orders/:id" element={<ProtectedRoute><OrderDetails /></ProtectedRoute>} />

                  {/* Deliveries */}
                  <Route path="deliveries"        element={<ProtectedRoute><Deliveries /></ProtectedRoute>} />
                  <Route path="collections"       element={<ProtectedRoute><Collections /></ProtectedRoute>} />
                  <Route path="activedeliveries"  element={<ProtectedRoute><ActiveDeliveries /></ProtectedRoute>} />
                  <Route path="delivery-dispatch" element={<ProtectedRoute><Dispatch /></ProtectedRoute>} />

                  {/* People */}
                  <Route path="drivers"              element={<ProtectedRoute><Drivers /></ProtectedRoute>} />
                  <Route path="customers"            element={<ProtectedRoute><Customers /></ProtectedRoute>} />
                  <Route path="customers/:id/orders" element={<ProtectedRoute><CustomerOrders /></ProtectedRoute>} />
                  <Route path="pickers"              element={<ProtectedRoute><Pickers /></ProtectedRoute>} />

                  {/* Analytics */}
                  <Route path="analytics" element={<ProtectedRoute allowedRoles={['admin', 'super']}><Analytics /></ProtectedRoute>} />

                  {/* Management */}
                  <Route path="create-user" element={<ProtectedRoute><CreateUser /></ProtectedRoute>} />
                  <Route path="help"        element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />

                  {/* Inventory */}
                  <Route path="products"                     element={<ProtectedRoute allowedRoles={['admin', 'super']}><ProductsPage /></ProtectedRoute>} />
                  <Route path="products/:productId/:storeId" element={<ProtectedRoute allowedRoles={['admin', 'super']}><PriceHistoryPage /></ProtectedRoute>} />
                  <Route path="categories"                   element={<ProtectedRoute allowedRoles={['admin', 'super']}><CategoriesPage /></ProtectedRoute>} />
                  <Route path="specials"                     element={<ProtectedRoute allowedRoles={['admin', 'super']}><SpecialsPage /></ProtectedRoute>} />
                  <Route path="stores"                       element={<ProtectedRoute allowedRoles={['admin', 'super']}><StoresPage /></ProtectedRoute>} />
                  <Route path="delivery-options"             element={<ProtectedRoute allowedRoles={['admin', 'super']}><DeliveryFeesPage /></ProtectedRoute>} />
                  <Route path="collection-points"            element={<ProtectedRoute allowedRoles={['admin', 'super']}><CollectionPointsPage /></ProtectedRoute>} />
                  <Route path="cake-config"                  element={<ProtectedRoute allowedRoles={['admin', 'super']}><CakeConfigPage /></ProtectedRoute>} />
                  <Route path="faqs"                         element={<ProtectedRoute allowedRoles={['admin', 'super']}><FaqsPage /></ProtectedRoute>} />
                </Route>
              </Routes>
            </BrowserRouter>
          </MaintenanceProvider>
        </AuthProvider>
      </Sentry.ErrorBoundary>
  );
}

export default App;